import './style.css';

import { bus } from './core/event-bus.js';
import { playerManager } from './core/player-manager.js';
import { buildSessionReport } from './core/report-builder.js';
import { initVastXmlParser } from './core/vast-xml-parser.js';

import { initAutomationApi } from './automation/api.js';
import { initDataBridge } from './automation/data-bridge.js';
import { readUrlParams } from './automation/url-params.js';

import { initSimulator, applyEnvironment } from './environments/simulator.js';

import { initTagInput, prettifyXml } from './panels/tag-input.js';
import { initControls } from './panels/controls.js';
import { initEventLogger } from './panels/event-logger.js';
import { initApiInspector } from './panels/api-inspector.js';
import { initNetworkMonitor } from './panels/network-monitor.js';
import { initCompanionPanel } from './panels/companion-panel.js';
import { initVastXmlInspector } from './panels/vast-xml-inspector.js';
import { initValidationPanel } from './panels/validation-panel.js';
import { initSessionPanel } from './panels/session-panel.js';
import { initEndCard } from './panels/endcard.js';
import { initDiagnosticsPanel } from './panels/diagnostics-panel.js';
import { initWorkflowSummary } from './panels/workflow-summary.js';

function initFooterYear() {
  const year = document.getElementById('app-footer-year');
  if (!year) return;
  year.textContent = new Date().getFullYear();
}

// ===== Tab Switching =====
function initTabs() {
  const tabs = document.querySelectorAll('.panel-tab');
  const panes = document.querySelectorAll('.panel-pane');

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const panelName = tab.getAttribute('data-panel');

      tabs.forEach(t => t.classList.toggle('active', t === tab));
      panes.forEach(p => p.classList.toggle('active', p.getAttribute('data-pane') === panelName));
    });
  });
}

// ===== Theme Toggle =====
function getPlayerPlaceholderPoster(theme) {
  if (theme === 'light') {
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 960 540'%3E%3Crect width='960' height='540' fill='%23e3efe8'/%3E%3Cg transform='translate(480 270)'%3E%3Cpath d='M-88 0C-88-42-48-54 0 0C48 54 88 42 88 0C88-42 48-54 0 0C-48 54-88 42-88 0Z' fill='none' stroke='%2323915f' stroke-width='3' stroke-linecap='round'/%3E%3C/g%3E%3C/svg%3E";
  }

  return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 960 540'%3E%3Crect width='960' height='540' fill='%2308110d'/%3E%3Cg transform='translate(480 270)'%3E%3Cpath d='M-88 0C-88-42-48-54 0 0C48 54 88 42 88 0C88-42 48-54 0 0C-48 54-88 42-88 0Z' fill='none' stroke='%2347c983' stroke-width='3' stroke-linecap='round'/%3E%3C/g%3E%3C/svg%3E";
}

function applyPlayerPlaceholderTheme(theme) {
  const video = document.getElementById('rmp-video');
  if (!video) return;
  video.setAttribute('poster', getPlayerPlaceholderPoster(theme));
}

function initTheme() {
  const toggle = document.querySelector('[data-testid="theme-toggle"]');
  const savedTheme = localStorage.getItem('vast-tester-theme');

  const updateThemeToggleLabel = (theme) => {
    if (!toggle) return;
    toggle.textContent = theme === 'light' ? '◑' : '◐';
  };

  if (savedTheme) {
    document.documentElement.setAttribute('data-theme', savedTheme);
  }

  const initialTheme = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  applyPlayerPlaceholderTheme(initialTheme);
  updateThemeToggleLabel(initialTheme);

  toggle?.addEventListener('click', () => {
    const current = document.documentElement.getAttribute('data-theme');
    const next = current === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('vast-tester-theme', next);
    applyPlayerPlaceholderTheme(next);
    updateThemeToggleLabel(next);
  });
}

// ===== Panel Resizing =====
function initResizablePanels() {
  const app = document.getElementById('app');
  const topPanels = document.querySelector('.top-panels');
  const mainArea = document.querySelector('.main-area');
  const rightPanel = document.querySelector('.api-inspector');
  const bottomPanels = document.querySelector('.bottom-panels');
  const handles = document.querySelectorAll('.resize-handle[data-resize]');

  if (!app || !topPanels || !mainArea || !rightPanel || !bottomPanels || handles.length === 0) {
    return;
  }

  const STORAGE_KEY = 'vast-tester-layout-sizes';
  const MIN_RIGHT_WIDTH = 220;
  const MAX_RIGHT_WIDTH_RATIO = 0.55;
  const MIN_BOTTOM_HEIGHT = 140;
  const MAX_BOTTOM_HEIGHT_RATIO = 0.55;
  const MIN_TOP_HEIGHT = 48;
  const MAX_TOP_HEIGHT_RATIO = 0.6;

  const rootStyle = document.documentElement.style;

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const getMinTopHeight = () => {
    const envBarHeight = document.querySelector('.env-bar')?.getBoundingClientRect().height ?? MIN_TOP_HEIGHT;
    return Math.max(MIN_TOP_HEIGHT, Math.ceil(envBarHeight));
  };

  const getMaxRightWidth = () => Math.floor(window.innerWidth * MAX_RIGHT_WIDTH_RATIO);
  const getMaxBottomHeight = () => Math.floor(window.innerHeight * MAX_BOTTOM_HEIGHT_RATIO);
  const getMaxTopHeight = () => Math.floor(window.innerHeight * MAX_TOP_HEIGHT_RATIO);

  const saveSizes = () => {
    const sizes = {
      right: parseInt(getComputedStyle(rightPanel).width, 10),
      bottom: parseInt(getComputedStyle(bottomPanels).height, 10)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes));
  };

  const applySize = (type, value) => {
    if (type === 'right') {
      const clamped = clamp(value, MIN_RIGHT_WIDTH, getMaxRightWidth());
      rootStyle.setProperty('--right-panel-width', `${clamped}px`);
      return;
    }

    if (type === 'bottom') {
      const clamped = clamp(value, MIN_BOTTOM_HEIGHT, getMaxBottomHeight());
      rootStyle.setProperty('--bottom-panels-height', `${clamped}px`);
      return;
    }

    if (type === 'top') {
      const clamped = clamp(value, getMinTopHeight(), getMaxTopHeight());
      rootStyle.setProperty('--top-panels-height', `${clamped}px`);
    }
  };

  const syncTopHeightWithContent = ({ allowShrink = false } = {}) => {
    if (dragState?.type === 'top') return;

    const currentHeight = Math.ceil(topPanels.getBoundingClientRect().height);
    const contentHeight = Math.ceil(topPanels.scrollHeight);
    const desiredHeight = allowShrink
      ? contentHeight
      : Math.max(currentHeight, contentHeight);

    if (!Number.isFinite(desiredHeight) || desiredHeight <= 0) return;
    if (Math.abs(desiredHeight - currentHeight) < 1) return;

    applySize('top', desiredHeight);
  };

  try {
    const savedSizesRaw = localStorage.getItem(STORAGE_KEY);
    if (savedSizesRaw) {
      const savedSizes = JSON.parse(savedSizesRaw);
      if (Number.isFinite(savedSizes?.right)) applySize('right', savedSizes.right);
      if (Number.isFinite(savedSizes?.bottom)) applySize('bottom', savedSizes.bottom);
    }
  } catch (error) {
    console.warn('[VAST Tester] Failed to restore panel sizes', error);
  }

  requestAnimationFrame(() => syncTopHeightWithContent({ allowShrink: true }));

  bus.on('layout:top-panels-content-changed', () => {
    requestAnimationFrame(() => syncTopHeightWithContent({ allowShrink: true }));
  });

  let dragState = null;

  const stopDrag = () => {
    if (!dragState) return;

    dragState.handle.classList.remove('is-active');
    document.body.classList.remove('is-resizing', 'is-resizing-horizontal', 'is-resizing-vertical');

    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);

    saveSizes();
    dragState = null;
  };

  const onPointerMove = (event) => {
    if (!dragState) return;

    if (dragState.type === 'right') {
      const nextWidth = dragState.startSize + (dragState.startClient - event.clientX);
      applySize('right', nextWidth);
      return;
    }

    if (dragState.type === 'bottom') {
      const nextHeight = dragState.startSize + (dragState.startClient - event.clientY);
      applySize('bottom', nextHeight);
      return;
    }
  };

  handles.forEach((handle) => {
    handle.addEventListener('pointerdown', (event) => {
      if (window.matchMedia('(max-width: 900px)').matches) return;

      const type = handle.getAttribute('data-resize');
      if (!type) return;

      event.preventDefault();

      let startSize = 0;
      let startClient = 0;
      let orientation = 'horizontal';

      if (type === 'right') {
        startSize = rightPanel.getBoundingClientRect().width;
        startClient = event.clientX;
        orientation = 'vertical';
      } else if (type === 'bottom') {
        startSize = bottomPanels.getBoundingClientRect().height;
        startClient = event.clientY;
      }

      dragState = {
        type,
        startSize,
        startClient,
        handle
      };

      handle.classList.add('is-active');
      document.body.classList.add('is-resizing', orientation === 'vertical' ? 'is-resizing-vertical' : 'is-resizing-horizontal');

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('pointercancel', stopDrag);
    });
  });

  window.addEventListener('resize', () => {
    applySize('right', parseInt(getComputedStyle(rightPanel).width, 10));
    applySize('bottom', parseInt(getComputedStyle(bottomPanels).height, 10));
    requestAnimationFrame(() => syncTopHeightWithContent({ allowShrink: true }));
  });
}

// ===== Auto-export for CI =====
function initAutoExport(urlParams) {
  if (!urlParams.export) return;

  bus.on('ad:event', ({ type }) => {
    if (type === 'adcomplete' || type === 'aderror' || type === 'addestroyed') {
      setTimeout(() => {
        const data = buildSessionReport();
        console.log('__VAST_TESTER_SESSION__', JSON.stringify(data));
      }, 1000);
    }
  });
}

// ===== Bootstrap =====
async function init() {
  const urlParams = readUrlParams();
  const profileOverrides = {};

  if (urlParams.muted !== null) {
    profileOverrides.muted = urlParams.muted;
  }
  if (urlParams.autoplay !== null) {
    profileOverrides.autoplay = urlParams.autoplay;
  }

  // Initialize all UI modules
  initFooterYear();
  initTheme();
  initResizablePanels();
  initTabs();
  initSimulator();
  initTagInput();
  initControls();
  initEventLogger();
  initApiInspector();
  initNetworkMonitor();
  initVastXmlParser();
  initCompanionPanel();
  initVastXmlInspector();
  initValidationPanel();
  initSessionPanel();
  initEndCard();
  initDiagnosticsPanel();
  initWorkflowSummary();

  // Initialize automation layer
  initAutomationApi();
  initDataBridge();
  initAutoExport(urlParams);

  // Apply environment from URL or default
  const env = urlParams.env || 'desktop';
  const hasProfileOverrides = Object.keys(profileOverrides).length > 0;
  await applyEnvironment(hasProfileOverrides ? { env, profileOverrides } : env);

  // Restore tag input from URL params
  const tagInput = document.getElementById('tag-url-input');
  let tagToLoad = null;

  if (urlParams.preset) {
    const { getPresetByName } = await import('./presets/registry.js');
    const preset = getPresetByName(urlParams.preset);
    if (preset) {
      tagInput.value = preset.url;
      tagToLoad = preset.url;
      // Select the preset in the dropdown
      const presetSelect = document.getElementById('preset-select');
      if (presetSelect) presetSelect.value = preset.name;
    }
  } else if (urlParams.tag) {
    tagInput.value = urlParams.tag;
    tagToLoad = urlParams.tag;
  } else if (urlParams.xml) {
    const rawXmlToggle = document.getElementById('raw-xml-toggle');
    const rawXmlContainer = document.getElementById('raw-xml-container');
    const rawXmlInput = document.getElementById('raw-xml-input');
    if (rawXmlToggle && rawXmlContainer && rawXmlInput) {
      rawXmlToggle.checked = true;
      rawXmlContainer.classList.remove('hidden');
      rawXmlInput.value = prettifyXml(urlParams.xml);
      bus.emit('layout:top-panels-content-changed');
    }
    tagToLoad = urlParams.xml;
  }

  // Auto-load if autostart is set or if a tag/preset was provided in the URL
  if (urlParams.autostart && tagToLoad) {
    if (urlParams.xml) {
      playerManager.loadRawXml(tagToLoad);
    } else {
      playerManager.loadAds(tagToLoad);
    }
  }

  console.log('[VAST Tester] Ready. Use window.VastTester for automation API.');
}

init().catch(console.error);
